/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Menu,
  ListSubheader,
  Typography,
  MenuItem,
  useTheme,
} from '@mui/material';
import React from 'react';
import { CustomIconButton, CustomTooltip } from '@opengeoweb/shared';
import { Options } from '@opengeoweb/theme';
import { useWorkspaceTranslation } from '../../utils/i18n';

export interface WorkspaceOptionsProps {
  id?: string;
  handleSave?: () => void;
  handleSaveAs?: () => void;
  handleEdit?: () => void;
  handleEditTitle?: () => void;
  handleDelete?: () => void;
  handleReset?: () => void;
  handleCopyToClipboard?: () => void;
  handlePasteFromClipboard?: () => void;
  copyToClipboardLabel?: string;
  pasteFromClipboardLabel?: string;
  isDisabled?: boolean;
  isDefaultOpen?: boolean;
  showEditTitleOption?: boolean;
}

export const workspaceOptionsButtonId = 'workspace-options-toolbutton';

export const WorkspaceOptions: React.FC<WorkspaceOptionsProps> = ({
  id = workspaceOptionsButtonId,
  handleSave,
  handleSaveAs,
  handleEdit,
  handleEditTitle,
  handleDelete,
  handleReset,
  handleCopyToClipboard,
  handlePasteFromClipboard,
  copyToClipboardLabel,
  pasteFromClipboardLabel,
  isDisabled = false,
  isDefaultOpen = false,
  showEditTitleOption = false,
}: WorkspaceOptionsProps) => {
  const { t } = useWorkspaceTranslation();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl) || isDefaultOpen;

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const options = [
    { title: t('workspace-save'), callback: handleSave },
    { title: t('workspace-save-as'), callback: handleSaveAs },
    { title: t('workspace-edit'), callback: handleEdit },
    ...(showEditTitleOption
      ? [{ title: t('workspace-edit-title'), callback: handleEditTitle }]
      : []),
    { title: t('workspace-reset'), callback: handleReset },
    {
      title: copyToClipboardLabel ?? t('workspace-copy-to-clipboard'),
      callback: handleCopyToClipboard,
    },
    {
      title: pasteFromClipboardLabel ?? t('workspace-paste-from-clipboard'),
      callback: handlePasteFromClipboard,
    },
    { title: t('workspace-delete'), callback: handleDelete },
  ];

  const renderButton = (): React.ReactElement => (
    <CustomIconButton
      aria-label="workspace options"
      data-testid={id}
      disabled={isDisabled}
      isSelected={isOpen}
      onClick={handleClick}
      sx={{
        '&&': {
          backgroundColor: !isOpen
            ? 'transparent'
            : 'geowebColors.workspace.tabButtonHover',
          '&:hover': {
            backgroundColor: 'geowebColors.workspace.tabButtonHover',
          },
          '.MuiSvgIcon-root > path': {
            fill: isOpen
              ? theme.palette.geowebColors.iconButtons.tool.active.color
              : undefined,
          },
        },
      }}
      variant="tool"
    >
      <Options />
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 21, horizontal: 6 }}
        onClose={() => setAnchorEl(null)}
        open={!!isOpen && !isDisabled}
        slotProps={{ backdrop: { style: { opacity: 0, transition: 'none' } } }}
        sx={{
          '& .MuiMenu-list': { padding: 0 },
          '& .MuiMenuItem-root': { fontSize: 16 },
        }}
      >
        <ListSubheader sx={{ paddingTop: '8px' }}>
          <Typography sx={{ letterSpacing: '0.4px', fontSize: 12 }}>
            {t('workspace-options')}
          </Typography>
        </ListSubheader>
        {options.map(({ title, callback }) => (
          <MenuItem disabled={!callback} key={title} onClick={callback}>
            {title}
          </MenuItem>
        ))}
      </Menu>
    </CustomIconButton>
  );

  return isDisabled ? (
    renderButton()
  ) : (
    <CustomTooltip
      slotProps={{
        transition: { exit: false },
      }}
      title={!isOpen && t('workspace-options')}
    >
      {renderButton()}
    </CustomTooltip>
  );
};
